import {
    createConversationThunk,
    fetchConversationThunk,
    fetchConversationsThunk,
    fetchMoreConversationMessagesThunk,
    fetchInboxesThunk,
    fetchUnreadCountThunk,
    lookupConversationThunk,
    markConversationReadThunk,
    OmnichannelState,
    OmnichannelStatus,
    sendMessageThunk
} from '@spike/omnichannel-action';
import { ChannelFilter, ConversationsFilter, InboxConversationSummary, InboxMessage } from '@spike/omnichannel-model';
import { showError } from '@spike/notifications-action';
import { useAuth } from 'hooks';
import { useDebounce } from 'hooks/useDebounce';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from 'store';
import { setCurrentInboxListFilter } from './inboxListFilter';
import { inboxRequestErrorMessage } from './InboxSettings/inboxRequestError';
import { canTextPhone, phoneDigits, Recipient, recipientToCreateInput } from './NewMessage/recipient';
import { useScopedApiClientWrapper } from './useScopedApiClientWrapper';

const LIST_PAGE_SIZE = 25;
// Thread pages come back at the backend's DEFAULT_SIZE; a shorter page means there is nothing older left.
const THREAD_PAGE_SIZE = 25;

// 403 = the omnichannel_inbox setting is off for that location (per-marketplace gate)
const listErrorMessage = (error: unknown): string =>
    (error as { response?: { status?: number } })?.response?.status === 403
        ? 'The Inbox is not enabled for this location yet.'
        : 'Conversations could not be loaded. Try again.';

/**
 * @param marketplaceId location whose inbox is shown; defaults to the login marketplace. Every request is
 * scoped to it by handing the thunks a wrapper with that `marketplaceId` (threads and inboxes are per
 * location, design doc D10). The WebSocket follows it through `MessagesChannelMarketplaceContext`.
 */
export const useInbox = (marketplaceId?: number) => {
    const dispatch = useDispatch<ThunkDispatch<RootState, null, AnyAction>>();
    const auth = useAuth();
    const location = useLocation<{ conversationId?: number }>();
    const omnichannel = useSelector<RootState, OmnichannelState>(state => state.omnichannel);

    const effectiveMarketplaceId = marketplaceId ?? auth.marketplaceId!;
    const scopedApiClientWrapper = useScopedApiClientWrapper(effectiveMarketplaceId);
    // callbacks below are created once; they read the wrapper through this ref so a location change is honoured
    const scopedApiClientWrapperRef = useRef(scopedApiClientWrapper);
    scopedApiClientWrapperRef.current = scopedApiClientWrapper;

    // "View client" comes back with the open thread id. The ref survives a strict-mode effect
    // replay; switching marketplace discards it.
    const restoredConversationIdRef = useRef(
        typeof location.state?.conversationId === 'number' ? location.state.conversationId : undefined
    );
    const restoredForMarketplaceIdRef = useRef(effectiveMarketplaceId);
    const [selectedId, setSelectedId] = useState<number | undefined>(restoredConversationIdRef.current);
    // "new conversation" mode: the thread pane shows the recipient picker instead of a thread
    const [composing, setComposing] = useState(false);
    const [composeError, setComposeError] = useState<string | null>(null);
    const [searchText, setSearchText] = useState('');
    const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all');
    const [sendError, setSendError] = useState<string | null>(null);
    const [listError, setListError] = useState<string | null>(null);
    const [newConversationIds, setNewConversationIds] = useState<Set<number>>(() => new Set());
    const [localNotes, setLocalNotes] = useState<Record<number, Array<InboxMessage>>>({});
    // page 1 is the most recent window (CUD-5721); older pages are prepended by the lib reducer
    const [olderPage, setOlderPage] = useState(1);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [hasOlder, setHasOlder] = useState(true);

    const selectedIdRef = useRef(selectedId);
    selectedIdRef.current = selectedId;
    const selectedConversationRef = useRef(omnichannel.selectedConversation);
    selectedConversationRef.current = omnichannel.selectedConversation;
    // Tail already shown for the open thread. The first load is seeded, not marked read again:
    // opening still goes through the selectedId effect. Only messages appended after that tail
    // (a live arrival, not an older page) can need their own POST /read.
    const seenConversationIdRef = useRef<number | undefined>(undefined);
    const openThreadTailIdRef = useRef<number | undefined>(undefined);
    const knownConversationIdsRef = useRef<Set<number>>(new Set());
    const seedListFromFetchRef = useRef(true);
    const authorNameRef = useRef('Staff');
    authorNameRef.current = [auth.user?.firstName, auth.user?.lastName].filter(Boolean).join(' ') || 'Staff';

    const debouncedSearch = useDebounce(searchText, 300);
    const listFilterRef = useRef<ConversationsFilter>({ page: 1, size: LIST_PAGE_SIZE });

    const listFilter = useMemo((): ConversationsFilter => {
        const filter: ConversationsFilter = { page: 1, size: LIST_PAGE_SIZE };

        if (channelFilter === 'sms' || channelFilter === 'email') {
            filter.channel = channelFilter;
        }
        if (channelFilter === 'unread') {
            filter.unread = true;
        }
        if (debouncedSearch.trim()) {
            filter.search = debouncedSearch.trim();
        }

        return filter;
    }, [channelFilter, debouncedSearch]);

    listFilterRef.current = listFilter;

    useEffect(() => {
        const sameMarketplace = effectiveMarketplaceId === restoredForMarketplaceIdRef.current;
        setSelectedId(sameMarketplace ? restoredConversationIdRef.current : undefined);

        if (!sameMarketplace) {
            restoredConversationIdRef.current = undefined;
            restoredForMarketplaceIdRef.current = effectiveMarketplaceId;
        }

        setComposing(false);
        setComposeError(null);
        setSendError(null);
        setNewConversationIds(new Set());
        knownConversationIdsRef.current = new Set();

        // thunks rethrow on failure (the reducer already records the error state) — swallow here
        dispatch(fetchInboxesThunk(scopedApiClientWrapper)).catch(() => undefined);
        dispatch(fetchUnreadCountThunk(scopedApiClientWrapper)).catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveMarketplaceId]);

    useEffect(() => {
        seedListFromFetchRef.current = true;
        setListError(null);
        setCurrentInboxListFilter(listFilter);
        // the reducer keeps the previous list on failure; the error hides it so a location that
        // rejects the request never shows another location's conversations
        dispatch(fetchConversationsThunk(scopedApiClientWrapper, listFilter)).catch(error => {
            setListError(listErrorMessage(error));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listFilter, effectiveMarketplaceId]);

    useEffect(() => {
        const ids = omnichannel.conversations.map(conversation => conversation.id);

        if (seedListFromFetchRef.current) {
            if (omnichannel.status === OmnichannelStatus.ConversationsFetchSuccess) {
                knownConversationIdsRef.current = new Set(ids);
                seedListFromFetchRef.current = false;
            }
            return;
        }

        const incomingIds = ids.filter(id => !knownConversationIdsRef.current.has(id));
        if (incomingIds.length === 0) {
            return;
        }

        incomingIds.forEach(id => knownConversationIdsRef.current.add(id));
        setNewConversationIds(prev => {
            const next = new Set(prev);
            incomingIds.forEach(id => next.add(id));
            return next;
        });
    }, [omnichannel.conversations, omnichannel.status]);

    useEffect(() => {
        if (selectedId === undefined) {
            return;
        }

        setOlderPage(1);
        setHasOlder(true);
        setLoadingOlder(false);

        const wrapper = scopedApiClientWrapperRef.current;
        dispatch(fetchConversationThunk(wrapper, selectedId))
            .then(() => {
                dispatch(markConversationReadThunk(wrapper, selectedId));
            })
            .catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    /** Previous page of the open thread, prepended by the reducer. Called when the user reaches the top. */
    const loadOlderMessages = useCallback(async () => {
        const conversationId = selectedIdRef.current;
        if (conversationId === undefined || loadingOlder || !hasOlder) return;

        const page = olderPage + 1;
        setLoadingOlder(true);
        try {
            const messages = await dispatch(
                fetchMoreConversationMessagesThunk(scopedApiClientWrapperRef.current, conversationId, page)
            );
            setOlderPage(page);
            if (messages.length < THREAD_PAGE_SIZE) setHasOlder(false);
        } catch {
            // a failed page must not lock the thread: the user can scroll up again to retry
        } finally {
            setLoadingOlder(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasOlder, loadingOlder, olderPage]);

    // A websocket message does not change selectedId, so the effect above never re-runs.
    // Mark the open thread read only while this tab is visible and focused.
    useEffect(() => {
        if (selectedId === undefined) {
            seenConversationIdRef.current = undefined;
            openThreadTailIdRef.current = undefined;
            return;
        }

        const conversation =
            omnichannel.selectedConversation?.id === selectedId ? omnichannel.selectedConversation : undefined;
        if (!conversation) {
            return;
        }

        const messages = conversation.messages;
        const tailId = messages[messages.length - 1]?.id;

        if (seenConversationIdRef.current !== selectedId) {
            seenConversationIdRef.current = selectedId;
            openThreadTailIdRef.current = tailId;
            return;
        }

        const previousTailId = openThreadTailIdRef.current;
        const previousTailIndex =
            previousTailId === undefined ? -1 : messages.findIndex(message => message.id === previousTailId);
        // The previous tail disappeared (thread replaced). Reseed and leave the open-thread POST as the only one.
        if (previousTailId !== undefined && previousTailIndex === -1) {
            openThreadTailIdRef.current = tailId;
            return;
        }

        const appended = previousTailId === undefined ? messages : messages.slice(previousTailIndex + 1);
        openThreadTailIdRef.current = tailId;

        const hasIncomingMessage = appended.some(
            message => message.direction === 'inbound' && message.kind === 'message'
        );
        const tabIsActive = document.visibilityState === 'visible' && document.hasFocus();
        if (!hasIncomingMessage || !tabIsActive) {
            return;
        }

        dispatch(markConversationReadThunk(scopedApiClientWrapperRef.current, selectedId)).catch(() => undefined);
    }, [dispatch, omnichannel.selectedConversation, selectedId]);

    /** Re-reads the open thread: attachment URLs are signed and expire, so a failed image reloads them. */
    const refreshSelectedConversation = useCallback(() => {
        const conversationId = selectedIdRef.current;
        if (conversationId === undefined) return;

        dispatch(fetchConversationThunk(scopedApiClientWrapperRef.current, conversationId)).catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectConversation = useCallback((id: number) => {
        setSelectedId(id);
        setComposing(false);
        setComposeError(null);
        setSendError(null);
        setNewConversationIds(prev => {
            if (!prev.has(id)) {
                return prev;
            }
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);

    const refreshAfterClientCreated = useCallback(
        async (phone: string) => {
            const wrapper = scopedApiClientWrapperRef.current;
            const currentId = selectedIdRef.current;
            const digits = phoneDigits(phone);

            try {
                const summaries = await dispatch(fetchConversationsThunk(wrapper, listFilterRef.current));
                const merged = summaries.find(
                    conversation =>
                        conversation.contact.type === 'customer' && phoneDigits(conversation.contact.phone) === digits
                );

                if (merged && merged.id !== currentId) {
                    selectConversation(merged.id);
                    return;
                }

                if (currentId !== undefined) {
                    await dispatch(fetchConversationThunk(wrapper, currentId));
                }
            } catch {
                dispatch(showError('The client was saved, but the conversation could not be refreshed.'));
            }
        },
        [selectConversation]
    );

    const startNewConversation = useCallback(() => {
        setSelectedId(undefined);
        setComposing(true);
        setComposeError(null);
    }, []);

    /**
     * Resolves the recipient's thread on the server (CUD-5723). A hit opens it; a 404 or any lookup
     * failure keeps the compose panel usable. A phone under 10 digits is not looked up (backend 422).
     */
    const openExistingConversation = useCallback(
        async (recipient: Recipient): Promise<boolean> => {
            if (recipient.kind === 'phone' && !canTextPhone(recipient.phone)) {
                return false;
            }

            try {
                const conversationId = await dispatch(
                    lookupConversationThunk(scopedApiClientWrapperRef.current, recipientToCreateInput(recipient))
                );
                if (conversationId == null) return false;
                selectConversation(conversationId);
                return true;
            } catch {
                return false;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectConversation]
    );

    const cancelNewConversation = useCallback(() => {
        setComposing(false);
        setComposeError(null);
    }, []);

    /**
     * First message of a new conversation (CUD-5587): nothing exists until now. POST /conversations is
     * idempotent (an existing thread for the contact comes back), then the regular send; the thread opens
     * in place. Resolves false when it failed so the Composer keeps the draft.
     */
    const sendFirstMessage = useCallback(
        async (recipient: Recipient, body: string, inboxId: number): Promise<boolean> => {
            const trimmed = body.trim();
            if (!trimmed) return false;

            const wrapper = scopedApiClientWrapperRef.current;
            setComposeError(null);
            try {
                const conversation = await dispatch(
                    createConversationThunk(wrapper, recipientToCreateInput(recipient))
                );
                await dispatch(sendMessageThunk(wrapper, { conversationId: conversation.id, body: trimmed, inboxId }));
                selectConversation(conversation.id);
                return true;
            } catch (error) {
                setComposeError(inboxRequestErrorMessage(error, 'The message could not be sent. Try again.'));
                return false;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectConversation]
    );

    const sendMessage = useCallback((body: string, inboxId: number) => {
        const trimmed = body.trim();
        const conversationId = selectedIdRef.current;
        if (!trimmed || conversationId === undefined) return;

        setSendError(null);

        dispatch(sendMessageThunk(scopedApiClientWrapperRef.current, { conversationId, body: trimmed, inboxId })).catch(
            () => {
                setSendError('Message could not be sent. Try again.');
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addNote = useCallback((body: string) => {
        const trimmed = body.trim();
        const conversationId = selectedIdRef.current;
        const conversation =
            selectedConversationRef.current?.id === conversationId ? selectedConversationRef.current : undefined;
        if (!trimmed || conversationId === undefined || !conversation) {
            return;
        }

        const note: InboxMessage = {
            id: -Date.now(),
            kind: 'note',
            channel: conversation.messages[conversation.messages.length - 1]?.channel || 'sms',
            direction: 'outbound',
            body: trimmed,
            authorName: authorNameRef.current,
            inboxId: conversation.inboxes[0]?.id ?? 0,
            fromIdentity: conversation.inboxes[0]?.fromIdentity || '',
            sentAt: moment().toISOString()
        };

        setLocalNotes(prev => ({
            ...prev,
            [conversationId]: [...(prev[conversationId] || []), note]
        }));
    }, []);

    const selectedConversation = useMemo(() => {
        const conversation =
            omnichannel.selectedConversation?.id === selectedId ? omnichannel.selectedConversation : undefined;
        if (!conversation) {
            return undefined;
        }

        const extras = localNotes[conversation.id];
        if (!extras?.length) {
            return conversation;
        }

        const existingIds = new Set(conversation.messages.map(message => message.id));
        const pending = extras.filter(note => !existingIds.has(note.id));
        if (pending.length === 0) {
            return conversation;
        }

        return {
            ...conversation,
            messages: [...conversation.messages, ...pending].sort((left, right) =>
                moment(left.sentAt).diff(moment(right.sentAt))
            )
        };
    }, [localNotes, omnichannel.selectedConversation, selectedId]);

    const conversations = useMemo(() => {
        if (listError) {
            return [];
        }

        const sorted = [...omnichannel.conversations].sort(
            (left: InboxConversationSummary, right: InboxConversationSummary) =>
                moment(right.lastMessage.sentAt).diff(moment(left.lastMessage.sentAt))
        );

        if (channelFilter === 'sms' || channelFilter === 'email') {
            return sorted.filter(conversation => conversation.lastMessage.channel === channelFilter);
        }

        return sorted;
    }, [channelFilter, listError, omnichannel.conversations]);

    return {
        marketplaceId: effectiveMarketplaceId,
        conversations,
        selectedConversation,
        selectedId,
        composing,
        composeError,
        searchText,
        channelFilter,
        totalUnread: omnichannel.unreadTotals.total,
        inboxes: omnichannel.inboxes,
        status: omnichannel.status,
        sendError,
        listError,
        isListLoading: omnichannel.status === OmnichannelStatus.ConversationsFetching,
        isThreadLoading: omnichannel.status === OmnichannelStatus.ConversationFetching,
        isSending:
            omnichannel.status === OmnichannelStatus.MessageSending ||
            omnichannel.status === OmnichannelStatus.ConversationCreating,
        newConversationIds,
        selectConversation,
        refreshSelectedConversation,
        loadOlderMessages,
        loadingOlder,
        hasOlder,
        refreshAfterClientCreated,
        startNewConversation,
        cancelNewConversation,
        openExistingConversation,
        sendFirstMessage,
        sendMessage,
        addNote,
        setSearchText,
        setChannelFilter
    };
};
